import { useMemo, useState, type ReactNode } from 'react'
import { Alert, Button, Checkbox } from 'antd'
import { AUTH_PRIMARY_BUTTON_CLASS } from '../../constants'
import { CardCvcElement, CardExpiryElement, CardNumberElement, Elements, useElements, useStripe } from '@stripe/react-stripe-js'
import { loadStripe } from '@stripe/stripe-js'
import type {
  StripeCardCvcElementChangeEvent,
  StripeCardElementOptions,
  StripeCardExpiryElementChangeEvent,
  StripeCardNumberElementChangeEvent,
} from '@stripe/stripe-js'

type CardField = 'number' | 'expiry' | 'cvc'

type CardFieldChangeEvent =
  | StripeCardNumberElementChangeEvent
  | StripeCardExpiryElementChangeEvent
  | StripeCardCvcElementChangeEvent

const CARD_FIELDS = ['number', 'expiry', 'cvc'] as const

const CARD_FIELD_PROMPTS: Record<CardField, string> = {
  number: 'Enter your card number',
  expiry: 'Enter the expiry date',
  cvc: 'Enter the CVC',
}

const emptyCardState = <T,>(value: T): Record<CardField, T> => ({ number: value, expiry: value, cvc: value })

const stripeElementOptions: StripeCardElementOptions = {
  style: {
    base: {
      color: '#111827',
      fontWeight: '400',
      fontFamily: 'inherit',
      fontSize: '14px',
      lineHeight: '24px',
      '::placeholder': { color: '#9ca3af' },
    },
    invalid: {
      color: '#cf1322',
    },
  },
}

export type PaymentStepProps = {
  loading: boolean
  skipPayment: boolean
  setSkipPayment: (value: boolean) => void
  isBasSignup: boolean
  hideBackButton: boolean
  onBack: () => void
  onSubmit: (paymentId: string | null) => Promise<void>
  clearError?: () => void
  stripePk: string
  summary?: ReactNode
  footer?: ReactNode
  submitLabel?: string
}

export default function PaymentStep({
  loading,
  skipPayment,
  setSkipPayment,
  isBasSignup,
  hideBackButton,
  onBack,
  onSubmit,
  clearError,
  stripePk,
  summary,
  footer,
  submitLabel,
}: PaymentStepProps) {
  const stripePromise = useMemo(() => (stripePk ? loadStripe(stripePk) : null), [stripePk])

  if (!stripePk) {
    return (
      <div className="space-y-[1rem]">
        {summary}
        <Alert
          type="warning"
          showIcon
          message="By clicking Next, your account will be created and you will not be able to go back a step."
        />
        <Alert
          type="warning"
          showIcon
          message="Missing VITE_STRIPE_PK. Configure Stripe publishable key to process card payments."
        />
        {isBasSignup && (
          <Checkbox checked={skipPayment} onChange={(e) => setSkipPayment(e.target.checked)}>
            Skip payment for now (create free account)
          </Checkbox>
        )}
        <div className="signup-step-actions">
          {!hideBackButton && <Button onClick={onBack} disabled={loading}>Back</Button>}
          <Button
            type="primary"
            disabled={!isBasSignup || !skipPayment}
            loading={loading}
            className={AUTH_PRIMARY_BUTTON_CLASS}
            onClick={() => void onSubmit(null)}
          >
            Next
          </Button>
        </div>
      </div>
    )
  }

  if (!stripePromise) return null

  return (
    <Elements stripe={stripePromise}>
      <StripePaymentForm
        loading={loading}
        skipPayment={skipPayment}
        setSkipPayment={setSkipPayment}
        isBasSignup={isBasSignup}
        hideBackButton={hideBackButton}
        onBack={onBack}
        onSubmit={onSubmit}
        clearError={clearError}
        summary={summary}
        footer={footer}
        submitLabel={submitLabel}
      />
    </Elements>
  )
}

function StripePaymentForm({
  loading,
  skipPayment,
  setSkipPayment,
  isBasSignup,
  hideBackButton,
  onBack,
  onSubmit,
  clearError,
  summary,
  footer,
  submitLabel,
}: Omit<PaymentStepProps, 'stripePk'>) {
  const stripe = useStripe()
  const elements = useElements()
  const [cardComplete, setCardComplete] = useState<Record<CardField, boolean>>(emptyCardState(false))
  const [cardErrors, setCardErrors] = useState<Record<CardField, string | null>>(emptyCardState<string | null>(null))
  const [cardError, setCardError] = useState<string | null>(null)
  // Held locally: `loading` only turns on inside onSubmit, leaving the card round
  // trip before it unguarded — and a second click there subscribes twice.
  const [submitting, setSubmitting] = useState(false)

  const handleFieldChange = (field: CardField) => (event: CardFieldChangeEvent) => {
    setCardComplete((prev) => ({ ...prev, [field]: event.complete }))
    setCardErrors((prev) => ({ ...prev, [field]: event.error?.message ?? null }))
    setCardError(null)
  }

  const focusField = (field: CardField) => {
    if (field === 'number') elements?.getElement(CardNumberElement)?.focus()
    else if (field === 'expiry') elements?.getElement(CardExpiryElement)?.focus()
    else elements?.getElement(CardCvcElement)?.focus()
  }

  const submitPayment = async () => {
    if (skipPayment) {
      await onSubmit(null)
      return
    }

    if (!stripe || !elements) {
      return
    }

    const card = elements.getElement(CardNumberElement)
    if (!card) {
      return
    }

    const incomplete = CARD_FIELDS.filter((field) => !cardComplete[field])
    const firstIncomplete = incomplete[0]
    if (firstIncomplete) {
      // Nothing reaches runAction on this path, so the last failure would sit
      // above the form contradicting the prompts below it.
      clearError?.()
      setCardErrors((prev) => {
        const next = { ...prev }
        incomplete.forEach((field) => {
          next[field] = prev[field] ?? CARD_FIELD_PROMPTS[field]
        })
        return next
      })
      focusField(firstIncomplete)
      return
    }

    const result = await stripe.createPaymentMethod({
      type: 'card',
      card,
    })

    // Thrown here it lands in an unhandled rejection — this runs outside runAction.
    if (result.error?.message || !result.paymentMethod?.id) {
      setCardError(result.error?.message || 'Unable to create payment method')
      return
    }

    setCardError(null)
    await onSubmit(result.paymentMethod.id)
  }

  const handleNext = async () => {
    if (submitting) return
    setSubmitting(true)
    try {
      await submitPayment()
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="space-y-[1rem]">
      {summary}
      <Alert
        type="warning"
        showIcon
        message="By clicking Next, your account will be created and you will not be able to go back a step."
      />

      {!skipPayment && (
        <>
          <div>
            <div className={`signup-stripe-field${cardErrors.number ? ' signup-stripe-field-invalid' : ''}`}>
              <div className="signup-stripe-field-label">Card Number</div>
              <CardNumberElement options={stripeElementOptions} onChange={handleFieldChange('number')} />
            </div>
            {cardErrors.number && (
              <div className="signup-stripe-field-error" role="alert">
                {cardErrors.number}
              </div>
            )}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <div className={`signup-stripe-field${cardErrors.expiry ? ' signup-stripe-field-invalid' : ''}`}>
                <div className="signup-stripe-field-label">Expiry Date</div>
                <CardExpiryElement options={stripeElementOptions} onChange={handleFieldChange('expiry')} />
              </div>
              {cardErrors.expiry && (
                <div className="signup-stripe-field-error" role="alert">
                  {cardErrors.expiry}
                </div>
              )}
            </div>
            <div>
              <div className={`signup-stripe-field${cardErrors.cvc ? ' signup-stripe-field-invalid' : ''}`}>
                <div className="signup-stripe-field-label">CVC</div>
                <CardCvcElement options={stripeElementOptions} onChange={handleFieldChange('cvc')} />
              </div>
              {cardErrors.cvc && (
                <div className="signup-stripe-field-error" role="alert">
                  {cardErrors.cvc}
                </div>
              )}
            </div>
          </div>
          {cardError && <Alert type="error" showIcon message={cardError} />}
        </>
      )}

      {isBasSignup && (
        <Checkbox checked={skipPayment} onChange={(e) => setSkipPayment(e.target.checked)}>
          Skip payment for now (create free account)
        </Checkbox>
      )}

      <div className="signup-step-actions">
        {!hideBackButton && <Button onClick={onBack} disabled={loading || submitting}>Back</Button>}
        <Button
          type="primary"
          loading={loading || submitting}
          disabled={loading || submitting}
          className={AUTH_PRIMARY_BUTTON_CLASS}
          onClick={() => void handleNext()}
        >
          {submitLabel ?? 'Next'}
        </Button>
      </div>

      {footer}
    </div>
  )
}
